import { Email, MoreVert, SupervisorAccount } from '@mui/icons-material';
import { Menu as MuiMenu } from '@mui/material';
import type { ElementType } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useImpersonate, useResendConfirmation } from '~/api/users';
import { IconButton } from '~/components/IconButton';
import { ActionButton } from '~/components/button';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { MoreAction, mergeGridActionProps, type GridActionCellProps } from '~/components/table';
import { useAppContext } from '~/contexts';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useMoreActions } from '~/hooks/table';
import { assert } from '~/lib/assert';
import type { NetworkUsers__NetworkMembership as NetworkMembership } from '../../queries/list.generated';

export interface MenuProps extends GridActionCellProps {
  buttonType?: 'action' | 'icon';
  networkMembership: NetworkMembership;
  Icon?: ElementType;
}

export const Menu = ({
  buttonType = 'icon',
  networkMembership,
  Icon = MoreVert,
  ...cellProps
}: MenuProps) => {
  const { t } = useTranslation();
  const { currentNetwork, setTransitioning } = useAppContext();
  const navigate = useNavigate();

  const [moreMenuProps, moreActionProps, moreTableActionProps] =
    useMoreActions<NetworkMembership>();

  const userActionProps = mergeGridActionProps(moreTableActionProps(networkMembership), cellProps);

  const [confirmImpersonate, confirmImpersonateProps] = useConfirmDialog();
  const [confirmResendConfirmation, confirmResendConfirmationProps] = useConfirmDialog();

  const [impersonate, { client }] = useImpersonate();
  const [resendConfirmation, { loading: resendingConfirmation }] = useResendConfirmation();

  const isConfirmed = moreMenuProps.context?.user.status === 'Active';

  return (
    <>
      {buttonType === 'icon' ? (
        <IconButton
          label={t('pages.users.list.actions.actions_tooltip')}
          onClick={userActionProps.onClick}
          size="small"
        >
          <Icon />
        </IconButton>
      ) : (
        <ActionButton {...userActionProps} />
      )}
      <MuiMenu {...moreMenuProps}>
        {currentNetwork.canManage.value
          ? [
              <MoreAction
                {...moreActionProps}
                key="impersonate"
                Icon={SupervisorAccount}
                disabled={!moreMenuProps.context?.canImpersonateUser.value}
                title={t('pages.users.list.actions.sign_in_as_user')}
                tooltip={
                  moreMenuProps.context?.canImpersonateUser.value
                    ? ''
                    : moreMenuProps.context?.canImpersonateUser.reasons?.fullMessages[0] ??
                      t('pages.users.list.actions.no_permission_impersonate')
                }
                onClick={async () => {
                  const id = moreMenuProps.context?.user.id;
                  assert(id != null, 'no user id');
                  if (!(await confirmImpersonate())) return;
                  await impersonate({ variables: { input: { id } } });
                  setTransitioning(true);
                  await client.resetStore();
                  navigate('..');
                  setTransitioning(false);
                }}
              />,
              <MoreAction
                {...moreActionProps}
                key="resend"
                Icon={Email}
                disabled={isConfirmed || resendingConfirmation}
                title={t('pages.users.list.actions.resend_confirmation')}
                tooltip={isConfirmed && t('pages.users.list.actions.user_already_confirmed')}
                onClick={async () => {
                  const userId = moreMenuProps.context?.user.id;
                  assert(userId != null, 'no user id');
                  if (!(await confirmResendConfirmation())) return;
                  void resendConfirmation({ variables: { input: { userId } } });
                }}
              />,
            ]
          : null}
      </MuiMenu>
      <ConfirmDialog
        {...confirmImpersonateProps}
        confirm={t('common.dialogs.confirm')}
        prompt={
          <Trans
            i18nKey="pages.users.dialogs.impersonate.prompt"
            values={{ name: moreMenuProps.context?.user.name }}
            components={{ strong: <strong /> }}
          />
        }
        title={t('pages.users.dialogs.impersonate.title')}
      />
      <ConfirmDialog
        {...confirmResendConfirmationProps}
        confirm={t('common.dialogs.confirm')}
        prompt={
          <Trans
            i18nKey="pages.users.dialogs.resend_confirmation.prompt"
            values={{ email: moreMenuProps.context?.user.email }}
            components={{ strong: <strong /> }}
          />
        }
        title={t('pages.users.dialogs.resend_confirmation.title')}
      />
    </>
  );
};
